import {useLatest} from "@hosttools/frontend";
import {VStack} from "@hosttools/frontend/components";
import {useAccount} from "@hosttools/frontend/react/hooks/useAccount";
import type React from "react";
import {memo, useCallback} from "react";

import useFetchListingsFromAccount from "../useFetchListingsFromAccount";

import ListingItem from "./ListingItem";
import ListingSkeleton from "./ListingSkeleton";

type Props = {
    accountID: string;
    checkedListings?: string[];
    multiSelect?: boolean;
    onChange: (accountID: string, checkedListings: string[]) => void;
};

const ListingList: React.FC<Props> = ({accountID, checkedListings = [], onChange, multiSelect}) => {
    const {data: listingsFromAccount, isLoading} = useFetchListingsFromAccount(accountID);
    const account = useAccount(accountID);
    const checkedListingsRef = useLatest(checkedListings);

    const handleChange = useCallback(
        (listingID: string, checked: boolean) => {
            if (multiSelect) {
                if (checked) {
                    onChange(accountID, [...checkedListingsRef.current, listingID]);
                } else {
                    onChange(
                        accountID,
                        checkedListingsRef.current.filter(id => id !== listingID)
                    );
                }
            } else {
                onChange(accountID, checked ? [listingID] : []);
            }
        },
        [accountID, checkedListingsRef, onChange, multiSelect]
    );

    if (!account) {
        return null;
    }

    return (
        <VStack space="sm">
            {isLoading ? (
                <>
                    {Array.from({length: 5}).map((_, index) => (
                        <ListingSkeleton key={index} />
                    ))}
                </>
            ) : (
                listingsFromAccount?.map(listing => (
                    <ListingItem
                        key={listing._id}
                        checked={checkedListings.includes(listing._id)}
                        connected={listing.isConnected}
                        listing={listing}
                        multiSelect={multiSelect}
                        onChange={handleChange}
                    />
                ))
            )}
        </VStack>
    );
};

export default memo(ListingList);
